import { Lecture } from '#types/lectures';
import { ReservedMembersType } from '#types/schedule';
import dayjs from 'dayjs';
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState
} from 'react';
import { useLocation } from 'react-router';

dayjs.locale('ko');

export interface WeekDataType {
  day: string;
  date: string;
}

export interface ScheduleLectureState
  extends Pick<
    ReservedMembersType,
    | 'lectureName'
    | 'lectureId'
    | 'personId'
    | 'personName'
    | 'round'
    | 'totalRound'
    | 'title'
    | 'memo'
    | 'repetition'
  > {
  activeDate: dayjs.Dayjs;
  startTime: string;
  endedTime: string;
  isGroup: boolean;
}

interface GroupMember {
  personName: string;
  personId: string;
  round: number;
  totalRound: number;
}

interface ScheduleManageContext {
  selectedLecture: ScheduleLectureState;
  setSelectedLecture: (newState: Partial<ScheduleLectureState>) => void;
  groupMembers: GroupMember[];
  setGroupMembers: Dispatch<SetStateAction<GroupMember[]>>;
  clearSelectedLecture: () => void;
  clearGroupMembers: () => void;
}

const ScheduleManageContext = createContext<ScheduleManageContext | null>(null);

const INITIAL_STATE: ScheduleLectureState = {
  activeDate: dayjs(),
  startTime: '06:00',
  endedTime: '07:00',
  lectureName: '',
  lectureId: '',
  personId: '',
  personName: '',
  round: 0,
  totalRound: 0,
  title: '',
  memo: '',
  isGroup: false,
  repetition: {
    type: 'WEEKLY',
    interval: 1,
    daysOfWeek: []
  }
};

export const useScheduleManage = () => {
  const context = useContext(ScheduleManageContext);
  if (!context) {
    throw new Error(
      'This component must be used within a <SchedulesManage> component.'
    );
  }
  return context;
};

function SchedulesManage({ children }: PropsWithChildren) {
  // 마지막으로 선택한 수업
  const lastSelectedLecture = JSON.parse(
    localStorage.getItem('selectedLecture') || '{}'
  ) as unknown as Lecture;

  const [selectedLecture, setSelectedLecture] =
    useState<ScheduleLectureState>(INITIAL_STATE);

  // 그룹 수업을 위한 회원 정보 리스트
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);

  const clearSelectedLecture = () => setSelectedLecture(INITIAL_STATE);

  const setLecture = (newState: Partial<ScheduleLectureState>) => {
    setSelectedLecture(prev => ({
      ...prev,
      ...newState
    }));
  };

  const clearGroupMembers = () => setGroupMembers([]);

  const { pathname } = useLocation();

  // 마지막으로 선택한 수업이 있을 경우, 해당 수업 정보를 초기화
  useEffect(() => {
    if (
      lastSelectedLecture &&
      !selectedLecture.lectureId &&
      !selectedLecture.lectureName
    ) {
      setLecture({
        lectureId: lastSelectedLecture.id,
        lectureName: lastSelectedLecture.title
      });
    }
  }, [pathname]);

  return (
    <ScheduleManageContext.Provider
      value={{
        selectedLecture,
        clearSelectedLecture,
        setSelectedLecture: setLecture,
        groupMembers,
        setGroupMembers,
        clearGroupMembers
      }}
    >
      {children}
    </ScheduleManageContext.Provider>
  );
}

export default SchedulesManage;
